import {Modal} from 'antd'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {NullAsDefault} from 'views/Shared'
import {showDeleteConfirmDialog} from '../../factory/createConfirmDialog'
import createContextMenu from '../../factory/createContextMenu'
import {notifyOnError} from '../../factory/createErrorEvent'
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import {MdOutlineControlPointDuplicate} from "react-icons/md";
import {renderIf} from "../../../Shared";
import useOrganizationTimetableApi from "./useOrganizationTimetableApi";
import {
    OrganizationTimetableCopyModal,
    OrganizationTimetableCreationModal,
    OrganizationTimetableEditingModal
} from "../components/OrganizationTimetableForm";
import {TimetableActionTypes} from "../../Course";

const useOrganizationTimetableForm = ({organizationDepartment, organization, ...props} = {}) => {
    const t = useTranslate()

    const [refreshKey, setRefreshKey] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onError = notifyOnError(t)

    const onCancel = () => {
        setAction({})
    }

    const onRefresh = () => {
        setRefreshKey(Date.now())
        onCancel()
    }

    const {
        createTimetable, updateTimetable, deleteTimetable
    } = useOrganizationTimetableApi({
        notifyListeners: {
            onRefresh,
            onCancel
        }
    })

    const handleAction = (action, params) => {
        switch (action) {
            case TimetableActionTypes.TIMETABLE_CREATE:
            case TimetableActionTypes.TIMETABLE_UPDATE:
            case TimetableActionTypes.TIMETABLE_DUPLICATE:
                setAction({type: action, value: params})
                break
            case TimetableActionTypes.TIMETABLE_DELETE:
                const id = params?.id
                if (id) {
                    showDeleteConfirmDialog({
                        translate: t,
                        title: 'delete timetable item',
                        message: 'are you sure you want to delete this timetable item',
                        onOk: () => {
                            deleteTimetable({}, {id: id})
                        },
                    })
                }
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <MdOutlineControlPointDuplicate size={14} className="text-green-500"/>,
                        label: 'duplicate',
                        key: TimetableActionTypes.TIMETABLE_DUPLICATE,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: TimetableActionTypes.TIMETABLE_UPDATE,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: TimetableActionTypes.TIMETABLE_DELETE,
                    },
                ].filter((e) => item),
                (key) => {
                    handleAction(key, item)
                }
            ),
        []
    )

    const modal = useMemo(() => {
        let title
        let Content = NullAsDefault
        let params = {title: undefined}
        let modal_params = {}

        const {Header = NullAsDefault} = props ?? {}

        const _organization = organizationDepartment?.organization ?? action?.value?.organization ?? organization ?? {}

        switch (action?.type) {
            case TimetableActionTypes.TIMETABLE_CREATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('add timetable')}
                    </div>
                )
                params = {
                    onCancel,
                    initialValues: {
                        organization: _organization,
                        organization_department_id: organizationDepartment?.id || action?.value?.organization_department_id,
                        organization_id: _organization?.id || action?.value?.organization_id
                    },
                    onSubmit: (values) => {
                        createTimetable(values, {
                            id: _organization?.id,
                        })
                    },
                }
                modal_params = {
                    width: 640,
                }
                Content = OrganizationTimetableCreationModal
                break
            case TimetableActionTypes.TIMETABLE_UPDATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit timetable')}
                    </div>
                )
                params = {
                    onCancel,
                    initialValues: action?.value,
                    onSubmit: (values) => {
                        updateTimetable(values, {
                            id: action?.value?.id,
                        })
                    },
                }
                modal_params = {
                    width: 700,
                }
                Content = OrganizationTimetableEditingModal
                break
            case TimetableActionTypes.TIMETABLE_DUPLICATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('duplicate timetable')}
                    </div>
                )
                params = {
                    onCancel,
                    initialValues: {
                        ...action?.value,
                        organization: _organization,
                        organization_department_id: organizationDepartment?.id,
                        organization_id: _organization?.id
                    },
                    onSubmit: (values) => {
                        createTimetable(values, {
                            id: _organization?.id,
                        })
                    },
                }
                modal_params = {
                    width: 700,
                }
                Content = OrganizationTimetableCopyModal
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <div className="flex flex-col gap-3">
                    {renderIf(
                        action?.type === TimetableActionTypes.TIMETABLE_DUPLICATE,
                        <div className="space-x-1 text-sm italic">
                            <span className="font-bold text-color-100">
                                {`* ${t('note')} : `}
                            </span>
                            <span className="text-color-400">
                                {t('duplicate timetable description')}
                            </span>
                        </div>
                    )}
                    <Header item={_organization}/>

                    <Content
                        {...params}
                    />
                </div>
            </Modal>
        )
    }, [organizationDepartment, action, props.Header])

    return {
        modal,
        action,
        onRefresh,
        onCancel,
        refreshKey,
        handleAction,
        renderContextMenu,
    }
}

export default useOrganizationTimetableForm
